import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Gift, Share2, ChevronRight } from 'lucide-react';

/**
 * Compact "invite a friend" nudge shown inside the match confirmation modal.
 *
 * Rendered inline below the primary Keep Swiping / Send Message CTAs. Fires
 * only after the user has a real match — the highest-conversion moment we
 * have to introduce the referral loop.
 *
 * Fetches the user's referral code lazily on mount (cached across mounts
 * server-side by referral_service.get_or_create_code). Silent if the request
 * fails — this is a non-critical secondary CTA and must never block the
 * celebration UI.
 */
export default function MatchReferralNudge() {
  const navigate = useNavigate();
  const [code, setCode] = useState(null);
  const [msg, setMsg] = useState('');
  const [url, setUrl] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/referral/my-code');
        if (!alive) return;
        setCode(res.data?.code);
        setMsg(res.data?.share_message || '');
        setUrl(res.data?.share_url || '');
      } catch {
        /* silent — the nudge just won't render */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!code) return null;

  const share = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Meet to Keep', text: msg, url });
      } catch { /* canceled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url || code);
        toast.success('Invite link copied');
      } catch {
        toast.error('Copy failed');
      }
    }
  };

  return (
    <div data-testid="match-referral-nudge"
      className="mt-5 pt-5 border-t border-black/[0.06] animate-fade-up relative z-10"
      style={{ animationDelay: '0.55s' }}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#FEF2F2] flex items-center justify-center flex-shrink-0">
          <Gift size={15} className="text-[#B91C1C]" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-[12.5px] font-semibold text-[#0F0F10] tracking-tight leading-tight">
            Know someone else who&apos;d take dating seriously?
          </p>
          <p className="text-[10.5px] text-[#8A8A93] leading-snug mt-0.5">
            Share code <span className="font-bold text-[#0F0F10]">{code}</span> — you both get 7 days Premium
          </p>
        </div>
        <button onClick={share} data-testid="match-share-invite-btn"
          className="flex-shrink-0 w-9 h-9 rounded-full bg-[#0F0F10] flex items-center justify-center text-white hover:bg-black active:scale-95 transition-all"
          aria-label="Share invite">
          <Share2 size={14} />
        </button>
      </div>
      <button onClick={() => navigate('/referrals')} data-testid="match-view-referrals-btn"
        className="mt-3 w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-[#8A8A93] hover:text-[#0F0F10] transition-colors">
        View invite history <ChevronRight size={11} />
      </button>
    </div>
  );
}
